import React, { useState, useEffect } from 'react';
import { InformePericial, Videoperitacion, FaseInforme } from '../../types';
import { informeService } from '../../services/api';
import { getAbsoluteUrl } from '../../utils/url';
import ImageLightbox from '../ImageLightbox';
import './EncargoForm.css';

interface PrevisualizacionContextoFaseProps {
    informe?: InformePericial;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

const accordionStyle: React.CSSProperties = {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '10px 14px',
    backgroundColor: '#ffffff',
    transition: 'all 0.15s ease'
};

const summaryStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    color: '#1e293b',
    outline: 'none'
};

const detailsContentStyle: React.CSSProperties = {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #f1f5f9',
    fontSize: '13px',
    color: '#334155',
    lineHeight: '1.6'
};

const PrevisualizacionContextoFase: React.FC<PrevisualizacionContextoFaseProps> = ({ informe, onUpdate }) => {
    const [videoperitacion, setVideoperitacion] = useState<Videoperitacion | null>(null);
    const [transcripcion, setTranscripcion] = useState<string | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        if (informe) {
            prvCargarDatos();
        }
    }, [informe]);

    const prvCargarDatos = async () => {
        if (!informe) return;
        try {
            const vpRes = await informeService.obtenerVideoperitacion(informe.id);
            setVideoperitacion(vpRes.data);
        } catch (e) {
            console.error('Error cargando videoperitación:', e);
        }

        try {
            const grabRes = await informeService.obtenerGrabacionPerito(informe.id);
            setTranscripcion(grabRes.data.transcripcion);
        } catch (e) {
            console.error('Error cargando grabación perito:', e);
        }
    };

    const prvHandleSiguienteFase = async () => {
        if (!informe) return;
        let updatedInforme = informe;
        try {
            const res = await informeService.cambiarFase(informe.id, 'informe');
            updatedInforme = res.data;
        } catch (e) {
            console.error('Error al cambiar de fase:', e);
        }
        onUpdate(updatedInforme, 'informe');
    };

    return (
        <div className="encargo-form">
            <div className="form-header-section flex justify-between items-center mb-4">
                <div>
                    <h3 className="section-title mb-1">Previsualización del Contexto para la IA</h3>
                </div>
                {informe && (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={prvHandleSiguienteFase}
                    >
                        Siguiente Fase
                    </button>
                )}
            </div>

            <div className="accordion-container mt-4" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Datos del Asegurado
                    </summary>
                    <div style={detailsContentStyle}>
                        <p><strong>Nombre y Apellidos:</strong> {informe?.nombre} {informe?.apellidos}</p>
                        <p><strong>Teléfono:</strong> {informe?.telefono}</p>
                        <p><strong>Dirección:</strong> {informe?.tipo_via || ''} {informe?.direccion}, {informe?.poblacion} ({informe?.provincia}), CP {informe?.codigo_postal}</p>
                        {informe?.contacto && <p><strong>Contacto Alternativo:</strong> {informe?.contacto}</p>}
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Preexistencias y Capitales (Póliza vs Perito)
                    </summary>
                    <div style={detailsContentStyle}>
                        <p><strong>Continente (Póliza):</strong> {informe?.preexistencias_continente || 'No especificado'}</p>
                        <p><strong>Continente Calculado (Perito):</strong> {informe?.preexistencias_continente_calculado || 'No especificado'}</p>
                        <p><strong>Contenido (Póliza):</strong> {informe?.preexistencias_contenido || 'No especificado'}</p>
                        <p><strong>Contenido Calculado (Perito):</strong> {informe?.preexistencias_contenido_calculado || 'No especificado'}</p>
                        <p><strong>Responsabilidad Civil (Póliza):</strong> {informe?.preexistencias_responsabilidad_civil || 'No especificado'}</p>
                        <p><strong>Responsabilidad Civil Calculada (Perito):</strong> {informe?.preexistencias_responsabilidad_civil_calculada || 'No especificado'}</p>
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Detalles del Siniestro y Póliza
                    </summary>
                    <div style={detailsContentStyle}>
                        <p><strong>Nº Expediente:</strong> {informe?.numero_expediente}</p>
                        <p><strong>Nº Siniestro:</strong> {informe?.numero_siniestro}</p>
                        <p><strong>Nº Póliza:</strong> {informe?.numero_poliza}</p>
                        <p><strong>Compañía Aseguradora:</strong> {informe?.compania} (Ramo: {informe?.ramo})</p>
                        <p><strong>Tipo Siniestro:</strong> {informe?.tipo}</p>
                        <p><strong>Fecha Encargo:</strong> {informe?.fecha_encargo ? new Date(informe.fecha_encargo).toLocaleDateString('es-ES') : '-'}</p>
                        <p><strong>Fecha Siniestro:</strong> {informe?.fecha_siniestro ? new Date(informe.fecha_siniestro).toLocaleDateString('es-ES') : '-'}</p>
                        <p><strong>Fecha Fin Servicio (Límite):</strong> {informe?.fecha_limite_entrega ? new Date(informe.fecha_limite_entrega).toLocaleDateString('es-ES') : 'Sin fecha límite'}</p>
                        {informe?.observaciones && (
                            <p style={{ marginTop: '8px', background: '#f8fafc', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0', margin: '8px 0 0 0' }}>
                                <strong>Observaciones iniciales:</strong> {informe.observaciones}
                            </p>
                        )}
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Datos Meteorológicos (Automáticos)
                    </summary>
                    <div style={detailsContentStyle}>
                        {informe?.datos_meteorologicos ? (
                            <div>
                                <p><strong>Temperatura Media:</strong> {informe.datos_meteorologicos.temperatura_media_celsius} °C</p>
                                <p><strong>Precipitación:</strong> {informe.datos_meteorologicos.precipitacion_mm} mm</p>
                                <p><strong>Velocidad Máx del Viento:</strong> {informe.datos_meteorologicos.viento_max_kmh} km/h</p>
                                <p><strong>Coordenadas Geocodificadas:</strong> Lat {informe.datos_meteorologicos.latitud}, Lon {informe.datos_meteorologicos.longitud}</p>
                                <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '8px' }}>
                                    Fuente: {informe.datos_meteorologicos.fuente}
                                </span>
                            </div>
                        ) : (
                            <p className="text-secondary" style={{ fontStyle: 'italic', margin: 0 }}>
                                No hay datos meteorológicos recopilados para este siniestro.
                            </p>
                        )}
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Nota de Voz del Perito (Texto Transcrito)
                    </summary>
                    <div style={detailsContentStyle}>
                        {transcripcion ? (
                            <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', background: '#f8fafc', padding: '10px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                {transcripcion}
                            </p>
                        ) : (
                            <p className="text-secondary" style={{ fontStyle: 'italic', margin: 0 }}>
                                No se ha grabado ni transcrito ninguna nota de voz aún.
                            </p>
                        )}
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Registro de Fotos ({videoperitacion?.imagenes_urls?.length || 0})
                    </summary>
                    <div style={detailsContentStyle}>
                        {videoperitacion?.imagenes_urls && videoperitacion.imagenes_urls.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                                {videoperitacion.imagenes_urls.map((imgUrl: string, idx: number) => {
                                    const absoluteUrl = getAbsoluteUrl(imgUrl);
                                    return (
                                        <div 
                                            key={idx} 
                                            style={{ border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden', cursor: 'pointer' }}
                                            onClick={() => setLightboxIndex(idx)}
                                            title="Haz clic para ampliar"
                                        >
                                            <img 
                                                src={absoluteUrl} 
                                                alt={`Siniestro foto ${idx+1}`} 
                                                style={{ width: '100%', height: '90px', objectFit: 'cover' }} 
                                            />
                                            <div style={{ padding: '4px', fontSize: '10px', textAlign: 'center', color: '#64748b' }}>
                                                Foto {idx+1}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-secondary" style={{ fontStyle: 'italic', margin: 0 }}>
                                No se han subido ni capturado imágenes en este expediente.
                            </p>
                        )}
                    </div>
                </details>

                <details style={accordionStyle} open>
                    <summary style={summaryStyle}>
                        Geolocalización GPS
                    </summary>
                    <div style={detailsContentStyle}>
                        {videoperitacion?.geolocalizacion_url || (informe?.datos_meteorologicos && (informe.datos_meteorologicos.latitud || informe.datos_meteorologicos.longitud)) ? (
                            <div>
                                <p><strong>Coordenadas estimadas de la peritación:</strong></p>
                                <p style={{ fontFamily: 'monospace', margin: '4px 0' }}>
                                    Latitud: {informe?.datos_meteorologicos?.latitud || 'Pendiente'} <br />
                                    Longitud: {informe?.datos_meteorologicos?.longitud || 'Pendiente'}
                                </p>
                                {videoperitacion?.geolocalizacion_url && (
                                    <p style={{ marginTop: '8px', margin: '8px 0 0 0' }}>
                                        <a 
                                            href={getAbsoluteUrl(videoperitacion.geolocalizacion_url)} 
                                            target="_blank" 
                                            rel="noreferrer"
                                            style={{ color: '#2563eb', textDecoration: 'underline', fontSize: '13px' }}
                                        >
                                            Ver archivo de geolocalización extraído
                                        </a>
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="text-secondary" style={{ fontStyle: 'italic', margin: 0 }}>
                                No hay coordenadas GPS extraídas para este siniestro.
                            </p>
                        )}
                    </div>
                </details>
            </div>

            {lightboxIndex !== null && videoperitacion?.imagenes_urls && (
                <ImageLightbox 
                    images={videoperitacion.imagenes_urls.map(url => getAbsoluteUrl(url))}
                    currentIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onIndexChange={(idx) => setLightboxIndex(idx)}
                />
            )}
        </div>
    );
};

export default PrevisualizacionContextoFase;
